// MACHINERY V2 (Stroje) — Diely a materiál: one installed-stock row (M2.5).
//
// Spec: data package §5. Reference impl:
// design_handoff_machinery_v3/machinery-profile-parts.jsx (`PcHead`, `PcRow`);
// artboards 1009 (desktop) and 1013 (phone).
//
// FIELDS THAT ARE FETCHED AND NOT SHOWN STAY FETCHED. §5 lists price, supplier,
// description, dimensions, condition and availability as "present, never shown".
// The row simply does not read them; the DTO is never trimmed, because the whole
// `InstalledStockItemStatsViewModel` is what the endpoint returns and what the
// slice stores. Do not "optimise" the fetch.
//
// IDENTICAL FOR CATALOG AND CUSTOM MACHINES. §5 catalog-vs-custom: the component
// takes only `machineId` and has no field divergence. There is no gate here and
// there must not be one.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { InstalledStockItem } from "../../../../inventory/models/models";
import FleetCardMenu, { FleetMenuAction } from "../../fleet/FleetCardMenu";
import { InstalledOverStock, PartCheckbox, PartThumb } from "./partsAtoms";

/** Desktop column template — artboard 1009 (PC_GRID in the handoff). */
export const PC_GRID = "34px 42px minmax(200px, 1.2fr) minmax(160px, 1fr) 170px 190px";

export interface PartRowProps {
  item: InstalledStockItem;
  selected: boolean;
  onToggleSelect: () => void;
  actions: FleetMenuAction[];
  onMarketplace: () => void;
  menuLabel: string;
  selectLabel: string;
}

const number = (item: InstalledStockItem) => item.stockItem?.product?.number || "";

export const PartRow: React.FC<PartRowProps> = ({
  item,
  selected,
  onToggleSelect,
  actions,
  onMarketplace,
  menuLabel,
  selectLabel,
}) => {
  const { translate } = useTranslation("myMachinery");
  const stock = item.stockItem;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: PC_GRID,
        gap: 12,
        padding: "9px 14px",
        alignItems: "center",
        borderTop: "1px solid var(--border-soft)",
        background: selected ? "var(--mod-accent-soft)" : "transparent",
        minWidth: 0,
      }}
    >
      <PartCheckbox checked={selected} label={selectLabel} onChange={onToggleSelect} />
      <PartThumb src={stock?.file?.content} />
      <span style={{ minWidth: 0 }}>
        <span
          style={{
            display: "block",
            fontFamily: "var(--font-mono)",
            fontSize: 12.5,
            fontWeight: 700,
            color: "var(--ink)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={number(item)}
        >
          {number(item)}
        </span>
        {stock?.alternativeNumber && (
          <span
            style={{
              display: "block",
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--faint)",
              marginTop: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {stock.alternativeNumber}
          </span>
        )}
      </span>
      <span style={{ minWidth: 0 }}>
        <span
          style={{
            display: "block",
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: "var(--ink-2)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {stock?.product?.manufacturer?.name}
        </span>
        <span
          style={{
            display: "block",
            fontSize: 11.5,
            color: "var(--muted)",
            marginTop: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {stock?.product?.category?.name}
        </span>
      </span>
      <InstalledOverStock installed={item.quantity} inStock={item.inStockQuantity} />
      <span style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onMarketplace}
          style={{
            color: "var(--mod-accent)",
            fontWeight: 600,
            height: 26,
            padding: "0 9px",
            whiteSpace: "nowrap",
          }}
        >
          {translate("myMachinery:v2.tabs.parts.marketplace")}
        </button>
        <FleetCardMenu actions={actions} ariaLabel={menuLabel} />
      </span>
    </div>
  );
};

/** Phone card (artboard 1013): thumb + number + brand·category + quantities. */
export const PartPhoneCard: React.FC<PartRowProps> = ({
  item,
  selected,
  onToggleSelect,
  actions,
  menuLabel,
  selectLabel,
}) => {
  const stock = item.stockItem;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: 10,
        borderRadius: 10,
        background: selected ? "var(--mod-accent-soft)" : "var(--card)",
        border: "1px solid " + (selected ? "var(--mod-accent-line)" : "var(--border)"),
        width: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
        minWidth: 0,
      }}
    >
      <PartCheckbox checked={selected} label={selectLabel} onChange={onToggleSelect} />
      <PartThumb src={stock?.file?.content} size={38} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            fontWeight: 700,
            color: "var(--ink)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {number(item)}
        </div>
        <div
          style={{
            fontSize: 11,
            color: "var(--muted)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {[stock?.product?.manufacturer?.name, stock?.product?.category?.name]
            .filter(Boolean)
            .join(" · ")}
        </div>
        <div style={{ marginTop: 2 }}>
          <InstalledOverStock installed={item.quantity} inStock={item.inStockQuantity} />
        </div>
      </div>
      <FleetCardMenu actions={actions} ariaLabel={menuLabel} />
    </div>
  );
};
